/**
 * 行数限制输入组件
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import { Bubble, Input } from 'tea-component';
import { useMemoizedFn } from 'ahooks';
import { ICountLimitProps } from './types';
import './index.scss';

const DEFAULT_MIN = 5000;
const DEFAULT_MAX = 50000;
const DEFAULT_VALUE = 5000;

const CountLimit: React.FC<ICountLimitProps> = (props) => {
  const {
    className,
    value,
    onChange,
    min = DEFAULT_MIN,
    max = DEFAULT_MAX,
    label,
    errorMessage,
    inputWidth = 85,
    defaultValue = DEFAULT_VALUE,
  } = props;
  const { formatMessage } = useIntl();
  const [isErrorVisible, setIsErrorVisible] = useState<boolean>(false);
  const [lastValidValue, setLastValidValue] = useState<number>(0);

  // 初始化 lastValidValue
  useEffect(() => {
    if (value >= min && value <= max) {
      setLastValidValue(value);
    } else if (value === 0 || !value) {
      setLastValidValue(defaultValue);
    }
  }, []);

  // 处理输入变化
  const handleChange = useMemoizedFn((inputValue: string) => {
    const valueNum = Number.parseInt(inputValue, 10);

    // 如果输入无效，不处理
    if (isNaN(valueNum)) {
      return;
    }

    // 更新值
    onChange(valueNum);

    // 检查是否超过最大值，显示错误提示
    if (valueNum > max) {
      setIsErrorVisible(true);
    } else {
      setIsErrorVisible(false);
    }

    // 保存有效值
    if (valueNum >= min && valueNum <= max) {
      setLastValidValue(valueNum);
    }
  });

  // 处理失去焦点
  const handleBlur = useMemoizedFn(() => {
    if (lastValidValue > 0) {
      // 如果有上次有效值，恢复到有效值
      onChange(lastValidValue);
      setIsErrorVisible(false);
      return;
    }

    // 如果值小于最小值，设置为默认值
    if (value < min) {
      onChange(defaultValue);
      setIsErrorVisible(false);
    }
  });

  // 处理回车键
  const handlePressEnter = useMemoizedFn((value: string, event: React.KeyboardEvent) => {
    event.preventDefault();
  });

  // 获取标签文本
  const labelText = useMemo(() => {
    if (label) {
      return formatMessage({ id: label });
    }
    return formatMessage({ id: 'dbl_chart_row_limit_info' });
  }, [label, formatMessage]);

  // 获取错误提示文本
  const errorText = useMemo(() => {
    if (errorMessage) {
      return formatMessage({ id: errorMessage });
    }
    return formatMessage({ id: 'dbl_chart_row_max_limit_info' });
  }, [errorMessage, formatMessage]);

  // 计算边框颜色
  const borderColor = useMemo(() => {
    if (isErrorVisible) {
      return '#ef5858';
    }
    if (value) {
      return '';
    }
    return '#326ff0';
  }, [isErrorVisible, value]);

  return (
    <div className={className || 'dbl-count-limit'}>
      <div className="dbl-count-limit-label">{labelText}</div>
      <Bubble
        error
        visible={isErrorVisible}
        placement="top"
        content={
          <div className="dbl-count-limit-error-tip">
            {errorText}
          </div>
        }
      >
        <Input
          value={String(value || '')}
          type="number"
          size="s"
          style={{
            width: typeof inputWidth === 'number' ? `${inputWidth}px` : inputWidth,
            borderColor,
          }}
          min={min}
          max={max}
          step={1}
          onBlur={handleBlur}
          onPressEnter={handlePressEnter}
          onChange={handleChange}
        />
      </Bubble>
    </div>
  );
};

export default CountLimit;
